import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { GET_ALL_INVOICES, GET_RECURRING_INVOICES } from '../graphql/query';
import {
  CREATE_RECURRING_INVOICE,
  DELETE_RECURRING_INVOICE,
  GENERATE_DUE_RECURRING_INVOICES,
} from '../graphql/mutation';
import { RecurringInvoiceInput, RecurringInvoicesData, RecurringInvoice } from '../types/interfaces';
import { PageHeader, ConfirmDialog, EmptyState } from '../components/common';
import RecurringInvoiceDetailDialog from '../components/RecurringInvoiceDetailDialog';
import TaskLineTypeFields, { taskLineYupSchema, emptyTaskLine, useDefaultLineType } from '../components/TaskLineTypeFields';

const validationSchema = yup.object({
  customerName: yup.string().required('Customer name is required'),
  customerABN: yup.string().required('Customer ABN is required'),
  customerAddress: yup.string().required('Customer address is required'),
  customerEmail: yup.string().email('Must be a valid email').required('Customer email is required'),
  projectName: yup.string().required('Project name is required'),
  projectDescription: yup.string().required('Project description is required'),
  discount: yup.number().min(0).max(100).required(),
  taxRate: yup.number().min(0).max(100).required(),
  frequency: yup.string().oneOf(['Monthly', 'Quarterly', 'Annually']).required('Frequency is required'),
  nextRunDate: yup.string().required('Next run date is required'),
  endDate: yup.string().nullable().optional(),
  tasks: yup.array().of(taskLineYupSchema).min(1, 'At least one task is required'),
});

const RecurringInvoicesPage: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [selectedRecurring, setSelectedRecurring] = useState<RecurringInvoice | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, data } = useQuery<RecurringInvoicesData>(GET_RECURRING_INVOICES);

  const [createRecurringInvoice, { loading: creating }] = useMutation(CREATE_RECURRING_INVOICE, {
    refetchQueries: [{ query: GET_RECURRING_INVOICES }],
  });
  const [deleteRecurringInvoice] = useMutation(DELETE_RECURRING_INVOICE, {
    refetchQueries: [{ query: GET_RECURRING_INVOICES }],
  });
  const [generateDueInvoices, { loading: generating }] = useMutation(GENERATE_DUE_RECURRING_INVOICES, {
    // Generation creates new invoices (dashboard + invoice list read GET_ALL_INVOICES from the
    // shared Apollo cache) and advances each template's NextRunDate/IsActive. Refetch both so the
    // dashboard stats and invoice list reflect the new runs without needing a hard refresh.
    refetchQueries: [{ query: GET_ALL_INVOICES }, { query: GET_RECURRING_INVOICES }],
    awaitRefetchQueries: true,
  });

  const defaultLineType = useDefaultLineType();

  const formik = useFormik<RecurringInvoiceInput>({
    enableReinitialize: true,
    initialValues: {
      customerName: '',
      customerABN: '',
      customerAddress: '',
      customerEmail: '',
      projectName: '',
      projectDescription: '',
      discount: 0,
      taxRate: 10,
      frequency: 'Monthly',
      nextRunDate: '',
      endDate: null,
      tasks: [emptyTaskLine(defaultLineType)],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const submissionValues = {
          ...values,
          taxRate: values.taxRate / 100,
          nextRunDate: new Date(values.nextRunDate + 'T00:00:00Z').toISOString(),
          endDate: values.endDate
            ? new Date(values.endDate + 'T00:00:00Z').toISOString()
            : null,
        };
        await createRecurringInvoice({ variables: { input: submissionValues } });
        enqueueSnackbar('Recurring invoice created successfully', { variant: 'success' });
        resetForm();
        setDialogOpen(false);
      } catch (err) {
        enqueueSnackbar(
          err instanceof Error ? err.message : 'Failed to create recurring invoice',
          { variant: 'error' }
        );
      }
    },
  });

  const handleDelete = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteRecurringInvoice({ variables: { id: confirmDelete.id } });
      enqueueSnackbar('Recurring invoice deleted', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete recurring invoice', { variant: 'error' });
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleGenerateDue = async () => {
    try {
      const { data } = await generateDueInvoices();
      const count = data?.generateDueRecurringInvoices ?? 0;
      if (count > 0) {
        enqueueSnackbar(`Generated ${count} invoice${count > 1 ? 's' : ''} and sent emails`, { variant: 'success' });
      } else {
        enqueueSnackbar('No recurring invoices are due yet', { variant: 'info' });
      }
    } catch (err) {
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Failed to generate due invoices',
        { variant: 'error' }
      );
    } finally {
      setConfirmGenerate(false);
    }
  };

  const handleAddTask = () => {
    formik.setFieldValue('tasks', [
      ...formik.values.tasks,
      emptyTaskLine(defaultLineType),
    ]);
  };

  const handleRemoveTask = (index: number) => {
    const tasks = [...formik.values.tasks];
    tasks.splice(index, 1);
    formik.setFieldValue('tasks', tasks);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    formik.resetForm();
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 2 }}>
        Error loading recurring invoices: {error.message}
      </Typography>
    );
  }

  const recurringInvoices = data?.recurringInvoices || [];

  return (
    <>
      <PageHeader
        title="Recurring Invoices"
        subtitle={`${recurringInvoices.length} template${recurringInvoices.length !== 1 ? 's' : ''}`}
        action={
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
              onClick={() => setConfirmGenerate(true)}
              disabled={generating || recurringInvoices.length === 0}
            >
              {generating ? 'Generating...' : 'Generate Due Invoices'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              New Recurring Invoice
            </Button>
          </Box>
        }
      />

      {recurringInvoices.length === 0 ? (
        <EmptyState
          icon={<AutorenewIcon sx={{ fontSize: 64 }} />}
          title="No recurring invoices"
          description="Create a recurring invoice template to automatically generate invoices on a schedule."
          action={{ label: 'Create Template', onClick: () => setDialogOpen(true) }}
        />
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Next Run</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recurringInvoices.map((ri) => (
                  <TableRow
                    key={ri.id}
                    hover
                    onClick={() => setSelectedRecurring(ri)}
                    sx={{ '&:last-child td': { borderBottom: 0 }, cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {ri.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ri.customerEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>{ri.projectName}</TableCell>
                    <TableCell>{ri.frequency}</TableCell>
                    <TableCell>{formatDate(ri.nextRunDate)}</TableCell>
                    <TableCell>{ri.endDate ? formatDate(ri.endDate) : 'No end'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={ri.isActive ? 'Active' : 'Paused'}
                        size="small"
                        sx={{
                          backgroundColor: ri.isActive ? '#2E7D32' : '#757575',
                          color: '#ffffff',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Delete recurring invoice">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setConfirmDelete({ open: true, id: ri.id })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <RecurringInvoiceDetailDialog
        invoice={selectedRecurring}
        open={!!selectedRecurring}
        onClose={() => setSelectedRecurring(null)}
      />

      {/* Create Recurring Invoice Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>New Recurring Invoice</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  id="customerName"
                  name="customerName"
                  label="Customer Name"
                  value={formik.values.customerName}
                  onChange={formik.handleChange}
                  error={formik.touched.customerName && Boolean(formik.errors.customerName)}
                  helperText={formik.touched.customerName && formik.errors.customerName}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  id="customerABN"
                  name="customerABN"
                  label="Customer ABN"
                  value={formik.values.customerABN}
                  onChange={formik.handleChange}
                  error={formik.touched.customerABN && Boolean(formik.errors.customerABN)}
                  helperText={formik.touched.customerABN && formik.errors.customerABN}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  id="customerAddress"
                  name="customerAddress"
                  label="Customer Address"
                  multiline
                  rows={2}
                  value={formik.values.customerAddress}
                  onChange={formik.handleChange}
                  error={formik.touched.customerAddress && Boolean(formik.errors.customerAddress)}
                  helperText={formik.touched.customerAddress && formik.errors.customerAddress}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  id="customerEmail"
                  name="customerEmail"
                  label="Customer Email"
                  type="email"
                  value={formik.values.customerEmail}
                  onChange={formik.handleChange}
                  error={formik.touched.customerEmail && Boolean(formik.errors.customerEmail)}
                  helperText={formik.touched.customerEmail && formik.errors.customerEmail}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  id="projectName"
                  name="projectName"
                  label="Project Name"
                  value={formik.values.projectName}
                  onChange={formik.handleChange}
                  error={formik.touched.projectName && Boolean(formik.errors.projectName)}
                  helperText={formik.touched.projectName && formik.errors.projectName}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  id="projectDescription"
                  name="projectDescription"
                  label="Project Description"
                  value={formik.values.projectDescription}
                  onChange={formik.handleChange}
                  error={formik.touched.projectDescription && Boolean(formik.errors.projectDescription)}
                  helperText={formik.touched.projectDescription && formik.errors.projectDescription}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  id="discount"
                  name="discount"
                  label="Discount (%)"
                  type="number"
                  value={formik.values.discount}
                  onChange={formik.handleChange}
                  error={formik.touched.discount && Boolean(formik.errors.discount)}
                  helperText={formik.touched.discount && formik.errors.discount}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  id="taxRate"
                  name="taxRate"
                  label="Tax Rate / GST (%)"
                  type="number"
                  value={formik.values.taxRate}
                  onChange={formik.handleChange}
                  error={formik.touched.taxRate && Boolean(formik.errors.taxRate)}
                  helperText={formik.touched.taxRate && formik.errors.taxRate}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  id="frequency"
                  name="frequency"
                  label="Frequency"
                  select
                  value={formik.values.frequency}
                  onChange={formik.handleChange}
                  error={formik.touched.frequency && Boolean(formik.errors.frequency)}
                  helperText={formik.touched.frequency && formik.errors.frequency}
                >
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Quarterly">Quarterly</MenuItem>
                  <MenuItem value="Annually">Annually</MenuItem>
                </TextField>
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  id="nextRunDate"
                  name="nextRunDate"
                  label="Next Run Date"
                  type="date"
                  value={formik.values.nextRunDate}
                  onChange={formik.handleChange}
                  error={formik.touched.nextRunDate && Boolean(formik.errors.nextRunDate)}
                  helperText={formik.touched.nextRunDate && formik.errors.nextRunDate}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  id="endDate"
                  name="endDate"
                  label="End Date (optional)"
                  type="date"
                  value={formik.values.endDate || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                  helperText={formik.touched.endDate && formik.errors.endDate}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>

              {/* Tasks */}
              {formik.values.tasks.map((task, index) => (
                <React.Fragment key={index}>
                  <Grid size={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Task {index + 1}</Typography>
                      {index > 0 && (
                        <IconButton size="small" onClick={() => handleRemoveTask(index)}>
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      id={`tasks[${index}].name`}
                      name={`tasks[${index}].name`}
                      label="Task Name"
                      value={task.name}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.tasks?.[index]?.name &&
                        Boolean((formik.errors.tasks?.[index] as any)?.name)
                      }
                      helperText={
                        formik.touched.tasks?.[index]?.name &&
                        (formik.errors.tasks?.[index] as any)?.name
                      }
                    />
                  </Grid>
                  <TaskLineTypeFields
                    index={index}
                    lineType={task.lineType ?? 'HOURLY'}
                    duration={task.duration ?? 0}
                    amount={task.amount ?? 0}
                    onChange={formik.handleChange}
                    touched={formik.touched.tasks?.[index]}
                    errors={formik.errors.tasks?.[index] as any}
                  />
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      id={`tasks[${index}].description`}
                      name={`tasks[${index}].description`}
                      label="Description"
                      value={task.description}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.tasks?.[index]?.description &&
                        Boolean((formik.errors.tasks?.[index] as any)?.description)
                      }
                      helperText={
                        formik.touched.tasks?.[index]?.description &&
                        (formik.errors.tasks?.[index] as any)?.description
                      }
                    />
                  </Grid>
                </React.Fragment>
              ))}

              <Grid size={12}>
                <Button startIcon={<AddIcon />} onClick={handleAddTask} variant="outlined">
                  Add Task
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={creating || !formik.isValid}
            >
              {creating ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Creating...
                </>
              ) : (
                'Create Recurring Invoice'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Delete Recurring Invoice"
        message="Are you sure you want to delete this recurring invoice template? This action cannot be undone."
        severity="error"
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ open: false, id: null })}
      />

      <ConfirmDialog
        open={confirmGenerate}
        title="Generate Due Invoices"
        message="This will generate invoices for all recurring templates that are due and send emails to customers. Continue?"
        severity="warning"
        confirmLabel="Generate & Send"
        onConfirm={handleGenerateDue}
        onCancel={() => setConfirmGenerate(false)}
      />
    </>
  );
};

export default RecurringInvoicesPage;
